import { useLiveQuery } from 'dexie-react-hooks'
import { useSyncExternalStore } from 'react'
import { getDb } from './db'
import { getMeta, type DeviceRegistration } from './meta'
import { countOutbox, type OutboxCounts } from './outbox'
import { countLostMedia, countPendingMedia } from './media'
import { openOutboxRecords, type OpenedOutboxRecord } from './sealed'
import { getSyncEngine } from './engine'
import type { SyncState } from './syncEngine'
import { env } from '@/lib/env'
import { rosterFreshness, type RosterFreshness } from './roster'

const EMPTY_COUNTS: OutboxCounts = { pending: 0, held: 0, errors: 0, setAside: 0, total: 0 }

export interface SyncStatus extends SyncState, OutboxCounts {
  mediaPending: number
  /** Photos whose bytes this phone can no longer read (shown as a problem, never retried alone). */
  mediaLost: number
  syncNow: () => Promise<void>
}

/** Live sync status for the indicator: engine phase + outbox counts straight from IndexedDB. */
export function useSyncStatus(): SyncStatus {
  const engine = getSyncEngine()
  const state = useSyncExternalStore(
    (cb) => engine.subscribe(cb),
    () => engine.getState(),
  )
  const counts = useLiveQuery(() => countOutbox(), [], EMPTY_COUNTS)
  const mediaPending = useLiveQuery(() => countPendingMedia(), [], 0)
  const mediaLost = useLiveQuery(() => countLostMedia(), [], 0)
  return { ...state, ...counts, mediaPending, mediaLost, syncNow: () => engine.syncNow() }
}

/**
 * The outbox, newest first, for the Sync screen. Payloads are opened; one no key opens is
 * listed with `payload_locked` (and a null payload) so the screen still shows it.
 */
export function useOutbox(limit = 100) {
  return useLiveQuery(
    async () => {
      const db = getDb()
      return openOutboxRecords(await db.outbox.orderBy('seq').reverse().limit(limit).toArray(), db)
    },
    [limit],
    [] as OpenedOutboxRecord[],
  )
}

export interface DeviceInfo {
  loaded: boolean
  deviceUid: string | null
  registration: DeviceRegistration | null
  rosterDownloadedAt: number | null
  rosterVersion: string | null
  freshness: RosterFreshness
}

/** Registration + roster age, live. */
export function useDeviceInfo(): DeviceInfo {
  const v = useLiveQuery(async () => {
    const [deviceUid, registration, rosterDownloadedAt, rosterVersion] = await Promise.all([
      getMeta('device_uid'),
      getMeta('device'),
      getMeta('roster_downloaded_at'),
      getMeta('roster_version'),
    ])
    return {
      loaded: true,
      deviceUid: deviceUid ?? null,
      registration: registration ?? null,
      rosterDownloadedAt: rosterDownloadedAt ?? null,
      rosterVersion: rosterVersion ?? null,
    }
  }, [])
  const base = v ?? {
    loaded: false,
    deviceUid: null,
    registration: null,
    rosterDownloadedAt: null,
    rosterVersion: null,
  }
  return { ...base, freshness: rosterFreshness(base.rosterDownloadedAt, env.rosterMaxAgeDays) }
}
